import { useEffect, useRef, useState } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

function formatTime(timestamp) {
  if (!timestamp?.seconds) return '';
  const d = new Date(timestamp.seconds * 1000);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function MessageThread({ requestId, senderRole }) {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const q = query(
      collection(db, 'requests', requestId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (!loading) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  async function handleSend(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'requests', requestId, 'messages'), {
        text: trimmed,
        senderEmail: currentUser.email,
        senderRole,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
      });
      setText('');
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  }

  const isMine = (msg) => msg.senderId === currentUser.uid;

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] flex flex-col">
      <div className="px-6 py-4 border-b border-[#F3F4F6]">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Messages</h3>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-[200px] max-h-[400px]">
        {loading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF] text-center py-6">
            No messages yet. Send one below to start the conversation.
          </p>
        ) : (
          messages.map((msg) => {
            const mine = isMine(msg);
            return (
              <div key={msg.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                  mine
                    ? 'bg-pb text-white rounded-br-sm'
                    : 'bg-[#F3F4F6] text-[#111827] rounded-bl-sm'
                }`}>
                  <p className="text-[13px] whitespace-pre-wrap break-words">{msg.text}</p>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  {!mine && (() => {
                    const roleMeta = {
                      staff:       { label: 'MST Staff',    cls: 'bg-[#0D2240] text-white' },
                      customer:    { label: 'Customer',     cls: 'bg-blue-light text-pb' },
                      supplier:    { label: 'Supplier',     cls: 'bg-amber-light text-[#92400E]' },
                      tour_leader: { label: 'Tour Leader',  cls: 'bg-green-light text-[#065F46]' },
                    };
                    const meta = roleMeta[msg.senderRole] || { label: msg.senderRole, cls: 'bg-[#F3F4F6] text-[#6B7280]' };
                    return (
                      <>
                        <span className="text-[12px] font-medium text-[#6B7280]">
                          {msg.senderEmail.split('@')[0]}
                        </span>
                        <span className={`text-[10px] font-bold px-[6px] py-[1px] rounded-[6px] ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </>
                    );
                  })()}
                  <span className="text-[12px] text-[#9CA3AF]">{formatTime(msg.createdAt)}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-[#F3F4F6]">
        <form onSubmit={handleSend} className="flex gap-2 items-end">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… (Enter to send)"
            rows={1}
            className="flex-1 border border-[#E5E7EB] rounded-[10px] px-4 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none leading-relaxed"
            style={{ minHeight: '42px', maxHeight: '120px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="px-4 py-2.5 text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[10px] transition disabled:opacity-50 flex-shrink-0"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
